import { ExprNode } from '@mode-switch/express';
import { Pipeline } from '../Pipeline';
export var DataRoles;
(function (DataRoles) {
    function make(catalog) {
        return (pipeline, node) => {
            switch (node.type) {
                case "NullLiteral":
                case "BooleanLiteral":
                case "NumberLiteral":
                case "HexLiteral":
                case "StringLiteral":
                case "RegExpLiteral":
                case "DateLiteral":
                case "LogicalExpression":
                    return Pipeline.DataRoles.set(pipeline, node, undefined);
                case "Identifier":
                case "AccessIdentifier":
                case "AccessExpression":
                    return vAccess(pipeline, catalog, node);
                case "CaseExpression":
                    return vCase(pipeline, node, catalog);
                case "CaseWhen":
                    return vCaseWhen(pipeline, node);
                case "CaseElse":
                    return vCaseElse(pipeline, node);
                case "IfExpression":
                    return vIf(pipeline, node, catalog);
                case "IfBase":
                    return vIfBase(pipeline, node);
                case "ElseIf":
                    return vElseIf(pipeline, node);
                case "IfElse":
                    return vIfElse(pipeline, node);
                case "CallExpression":
                    return vCall(pipeline, catalog, node);
                case "UnaryExpression":
                    return vUnary(pipeline, catalog, node);
                case "BinaryExpression":
                    return vBinary(pipeline, catalog, node);
                default:
                    return pipeline;
            }
        };
    }
    DataRoles.make = make;
    function vAccess(pipeline, catalog, node) {
        const param = Pipeline.Params.get(pipeline, node);
        if (param != null) {
            return Pipeline.DataRoles.set(pipeline, node, param.formula.role);
        }
        const column = Pipeline.Columns.get(pipeline, node);
        if (column != null) {
            return Pipeline.DataRoles.set(pipeline, node, column.formula.role);
        }
        return pipeline;
    }
    function vUnary(pipeline, catalog, node) {
        const operand = Pipeline.DataRoles.get(pipeline, node.operand);
        if (operand == null) {
            return pipeline;
        }
        switch (node.operator) {
            case '-':
                return Pipeline.DataRoles.set(pipeline, node, catalog.getNegatedRole(operand));
            case '+':
                return Pipeline.DataRoles.set(pipeline, node, catalog.getPlussedRole(operand));
            default:
                return pipeline;
        }
    }
    function vBinary(pipeline, catalog, node) {
        const left = Pipeline.DataRoles.get(pipeline, node.left);
        const right = Pipeline.DataRoles.get(pipeline, node.right);
        switch (node.operator) {
            case '+':
                return Pipeline.DataRoles.set(pipeline, node, catalog.getAddedRole(left, right));
            case '-':
            case '*':
            case '/':
            case '%':
                return Pipeline.DataRoles.set(pipeline, node, catalog.getMathedRole(left, right));
            case '=':
            case '<>':
            case '<':
            case '<=':
            case '>':
            case '>=':
            default:
                return Pipeline.DataRoles.set(pipeline, node, undefined);
        }
    }
    function vCase(pipeline, node, catalog) {
        let lastRole;
        for (const caseNode of node.cases) {
            const caseRole = Pipeline.DataRoles.get(pipeline, caseNode);
            if (caseRole === undefined) {
                return pipeline;
            }
            if (lastRole === undefined || catalog.roleEquals(lastRole, caseRole)) {
                lastRole = caseRole;
            }
            else {
                return pipeline;
            }
        }
        if (node.else != null) {
            const elseRole = Pipeline.DataRoles.get(pipeline, node.else);
            if (elseRole === undefined) {
                return pipeline;
            }
            if (lastRole === undefined || catalog.roleEquals(lastRole, elseRole)) {
                lastRole = elseRole;
            }
            else {
                return pipeline;
            }
        }
        if (lastRole === undefined) {
            return pipeline;
        }
        else {
            return Pipeline.DataRoles.set(pipeline, node, lastRole);
        }
    }
    function vCaseWhen(pipeline, node) {
        const resultRole = Pipeline.DataRoles.get(pipeline, node.result);
        if (resultRole == null) {
            return pipeline;
        }
        return Pipeline.DataRoles.set(pipeline, node, resultRole);
    }
    function vCaseElse(pipeline, node) {
        const resultRole = Pipeline.DataRoles.get(pipeline, node.result);
        if (resultRole == null) {
            return pipeline;
        }
        return Pipeline.DataRoles.set(pipeline, node, resultRole);
    }
    function vIf(pipeline, node, catalog) {
        let lastRole;
        const baseRole = Pipeline.DataRoles.get(pipeline, node.base);
        if (baseRole === undefined) {
            return pipeline;
        }
        else {
            lastRole = baseRole;
        }
        for (const elseifNode of node.elses) {
            const elseifRole = Pipeline.DataRoles.get(pipeline, elseifNode);
            if (elseifRole === undefined) {
                return pipeline;
            }
            if (lastRole === undefined || catalog.roleEquals(lastRole, elseifRole)) {
                lastRole = elseifRole;
            }
            else {
                return pipeline;
            }
        }
        if (node.else != null) {
            const elseRole = Pipeline.DataRoles.get(pipeline, node.else);
            if (elseRole === undefined) {
                return pipeline;
            }
            if (lastRole === undefined || catalog.roleEquals(lastRole, elseRole)) {
                lastRole = elseRole;
            }
            else {
                return pipeline;
            }
        }
        if (lastRole === undefined) {
            return pipeline;
        }
        else {
            return Pipeline.DataRoles.set(pipeline, node, lastRole);
        }
    }
    function vIfBase(pipeline, node) {
        const resultType = Pipeline.DataRoles.get(pipeline, node.result);
        if (resultType == null) {
            return pipeline;
        }
        return Pipeline.DataRoles.set(pipeline, node, resultType);
    }
    function vElseIf(pipeline, node) {
        const resultType = Pipeline.DataRoles.get(pipeline, node.result);
        if (resultType == null) {
            return pipeline;
        }
        return Pipeline.DataRoles.set(pipeline, node, resultType);
    }
    function vIfElse(pipeline, node) {
        const resultType = Pipeline.DataRoles.get(pipeline, node.result);
        if (resultType == null) {
            return pipeline;
        }
        return Pipeline.DataRoles.set(pipeline, node, resultType);
    }
    function vCall(pipeline, catalog, node) {
        const argNodes = [];
        const argRoles = [];
        for (const arg of node.arguments) {
            const argNode = Pipeline.ExprNodes.get(pipeline, arg);
            if (argNode == null) {
                throw new Error(`Cannot resolve call argument ${argNode} of ${node.callee}`);
            }
            argNodes.push(argNode);
            argRoles.push(Pipeline.DataRoles.get(pipeline, arg));
        }
        const callName = ExprNode.CallName.make(node.callee);
        return Pipeline.DataRoles.set(pipeline, node, catalog.getExprCallRole(callName, argNodes, argRoles));
    }
})(DataRoles || (DataRoles = {}));
